import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Search, ChevronDown, ChevronLeft, ChevronRight, Calendar, X, User } from 'lucide-react';
import { api } from '../services/api';
import { formatDateTime } from '../utils/dateFormat';
import ScrollToTop from './ScrollToTop';
import type { AuditLog, AuditUser, PagedResult } from '../types';

const PAGE_SIZE = 25;

const ACTION_TYPE_LABELS: Record<string, string> = {
  USER_LOGIN: 'User Login',
  USER_LOGOUT: 'User Logout',
  ROUND_CREATED: 'Round Created',
  ROUND_STATUS_CHANGED: 'Round Status Changed',
  EMPLOYEE_SYNC_MANUAL: 'Manual Employee Sync',
  EMPLOYEE_SYNC_AUTO: 'Auto Employee Sync',
  PHOTO_SYNC_MANUAL: 'Manual Photo Sync',
  EMPLOYEE_ROLE_UPDATED: 'Employee Role Updated',
  SETTINGS_UPDATED: 'Settings Updated',
  DATA_EXPORTED: 'Data Exported',
  CREATE_ROUND: 'Round Created',
  ROUND_DELETED: 'Round Deleted',
};

const ACTION_TYPE_COLORS: Record<string, string> = {
  USER_LOGIN: 'bg-blue-100 text-blue-800',
  USER_LOGOUT: 'bg-slate-100 text-slate-700',
  ROUND_CREATED: 'bg-green-100 text-green-800',
  CREATE_ROUND: 'bg-green-100 text-green-800',
  ROUND_STATUS_CHANGED: 'bg-yellow-100 text-yellow-800',
  EMPLOYEE_SYNC_MANUAL: 'bg-purple-100 text-purple-800',
  EMPLOYEE_SYNC_AUTO: 'bg-purple-100 text-purple-800',
  PHOTO_SYNC_MANUAL: 'bg-fuchsia-100 text-fuchsia-800',
  EMPLOYEE_ROLE_UPDATED: 'bg-indigo-100 text-indigo-800',
  SETTINGS_UPDATED: 'bg-orange-100 text-orange-800',
  DATA_EXPORTED: 'bg-gray-100 text-gray-800',
  ROUND_DELETED: 'bg-red-100 text-red-800',
};

const AuditLogTab: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedActionType, setSelectedActionType] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | ''>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [actionTypes, setActionTypes] = useState<string[]>([]);
  const [auditUsers, setAuditUsers] = useState<AuditUser[]>([]);

  // Load filter options
  useEffect(() => {
    api.getAuditActionTypes()
      .then(setActionTypes)
      .catch(err => console.error('Error loading action types:', err));
    api.getAuditUsers()
      .then(setAuditUsers)
      .catch(err => console.error('Error loading audit users:', err));
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, selectedActionType, selectedEmployeeId, startDate, endDate]);

  // Fetch audit logs
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const result: PagedResult<AuditLog> = await api.getAuditLogs(
        page,
        PAGE_SIZE,
        selectedActionType || undefined,
        debouncedSearch || undefined,
        startDate || undefined,
        endDate || undefined,
        selectedEmployeeId || undefined
      );
      setLogs(result.items);
      setTotalCount(result.totalCount);
      setTotalPages(result.totalPages);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, selectedActionType, selectedEmployeeId, startDate, endDate]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);


  const hasFilters = selectedActionType || selectedEmployeeId || startDate || endDate || searchTerm;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-lg">
          <FileText className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Audit Log</h2>
          <p className="text-sm text-gray-500 mt-0.5">{totalCount.toLocaleString()} total entries</p>
        </div>
      </div>

      {/* Responsive: filters wrap on mobile */}
      <div className="bg-white rounded-xl shadow-lg p-3 sm:p-5 border border-gray-100">
        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative w-full sm:w-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search descriptions or users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-56 shadow-sm text-sm min-h-[44px]"
            />
          </div>

          {/* User */}
          <div className="relative w-full xs:w-auto">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <select
              value={selectedEmployeeId}
              onChange={(e) => setSelectedEmployeeId(e.target.value ? Number(e.target.value) : '')}
              className="appearance-none pl-9 pr-8 py-2 bg-white border-2 border-gray-200 rounded-lg text-sm font-medium text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm cursor-pointer w-full xs:min-w-[150px] min-h-[44px]"
            >
              <option value="">All Users</option>
              {auditUsers.map(user => (
                <option key={user.employeeID} value={user.employeeID}>
                  {user.name}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Action Type */}
          <div className="relative w-full xs:w-auto">
            <select
              value={selectedActionType}
              onChange={(e) => setSelectedActionType(e.target.value)}
              className="appearance-none pl-3 pr-8 py-2 bg-white border-2 border-gray-200 rounded-lg text-sm font-medium text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm cursor-pointer w-full xs:min-w-[160px] min-h-[44px]"
            >
              <option value="">All Action Types</option>
              {actionTypes.map(type => (
                <option key={type} value={type}>
                  {ACTION_TYPE_LABELS[type] || type}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Responsive: date range wraps on small screens */}
          <div className="flex flex-wrap items-center gap-1.5 w-full xs:w-auto">
            <Calendar className="w-4 h-4 text-gray-500 shrink-0" />
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-2 py-2 border-2 border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm flex-1 xs:flex-none xs:w-36 min-h-[44px]"
            />
            <span className="text-gray-400 text-sm">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-2 py-2 border-2 border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm flex-1 xs:flex-none xs:w-36 min-h-[44px]"
            />
          </div>

          {/* Clear Filters */}
          {hasFilters && (
            <button
              onClick={() => {
                setSelectedActionType('');
                setSelectedEmployeeId('');
                setStartDate('');
                setEndDate('');
                setSearchTerm('');
              }}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer shrink-0"
            >
              <X className="w-4 h-4" />
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto mb-3"></div>
          <p className="text-gray-500 text-sm">Loading audit logs...</p>
        </div>
      )}

      {!loading && logs.length > 0 && (
        <>
          {/* Mobile: card-based audit log */}
          <div className="md:hidden space-y-3">
            {logs.map(log => (
              <div key={log.auditID} className="bg-white rounded-xl shadow-md p-4 border border-gray-100">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                    ACTION_TYPE_COLORS[log.actionType] || 'bg-gray-100 text-gray-800'
                  }`}>
                    {ACTION_TYPE_LABELS[log.actionType] || log.actionType}
                  </span>
                  <span className="text-xs text-gray-400">{formatDateTime(log.actionDate)}</span>
                </div>
                <p className="text-sm font-medium text-gray-900 mb-1">
                  {log.employeeName || <span className="text-gray-400">System</span>}
                </p>
                {log.actionDescription && (
                  <p className="text-sm text-gray-600 mb-1">{log.actionDescription}</p>
                )}
                <div className="flex items-center gap-3 text-xs text-gray-400 mt-2">
                  {log.entityType && <span>{log.entityType}</span>}
                  {log.ipAddress && <span className="font-mono">{log.ipAddress}</span>}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: full table layout */}
          <div className="hidden md:block bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
            <table className="w-full table-fixed">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                <tr>
                  <th style={{ width: '16%' }} className="px-5 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Date / Time
                  </th>
                  <th style={{ width: '14%' }} className="px-5 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    User
                  </th>
                  <th style={{ width: '16%' }} className="px-5 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Action
                  </th>
                  <th style={{ width: '32%' }} className="px-5 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Description
                  </th>
                  <th style={{ width: '12%' }} className="px-5 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Entity
                  </th>
                  <th style={{ width: '10%' }} className="px-5 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    IP Address
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.map(log => (
                  <tr key={log.auditID} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-3">
                      <span className="text-sm text-gray-700 block truncate" title={formatDateTime(log.actionDate)}>{formatDateTime(log.actionDate)}</span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-sm font-medium text-gray-900 block truncate" title={log.employeeName || '---'}>
                        {log.employeeName || <span className="text-gray-400">System</span>}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`inline-block max-w-full truncate px-2.5 py-1 rounded-full text-xs font-semibold ${
                        ACTION_TYPE_COLORS[log.actionType] || 'bg-gray-100 text-gray-800'
                      }`} title={ACTION_TYPE_LABELS[log.actionType] || log.actionType}>
                        {ACTION_TYPE_LABELS[log.actionType] || log.actionType}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-sm text-gray-600 block truncate" title={log.actionDescription || ''}>
                        {log.actionDescription || '---'}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-sm text-gray-500 block truncate" title={log.entityType || '---'}>
                        {log.entityType || '---'}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-xs font-mono text-gray-500 block truncate" title={log.ipAddress || '---'}>{log.ipAddress || '---'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Responsive: pagination wraps on mobile */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm text-gray-600">
              Showing {((page - 1) * PAGE_SIZE) + 1} to {Math.min(page * PAGE_SIZE, totalCount)} of {totalCount.toLocaleString()} entries
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="px-4 py-2 text-sm font-medium text-gray-700">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Empty State */}
      {!loading && logs.length === 0 && (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center border border-gray-100">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Audit Entries Found</h3>
          <p className="text-gray-500">
            {hasFilters
              ? 'Try adjusting your filters to find what you\'re looking for.'
              : 'No audit events have been recorded yet.'}
          </p>
        </div>
      )}
      <ScrollToTop />
    </div>
  );
};

export default AuditLogTab;
